import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { WarningAmberOutlined } from '@mui/icons-material';
import { Button, Chip, LinearProgress, Skeleton, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import Box from '@mui/material/Box';
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutline';
import BarChartIcon from '@mui/icons-material/BarChart';
import ChartBanner from './ChartBanner';
import { formatValue, normalize, percentageFormat } from '../../../../helpers/helpers';
import { ChartType } from '../../../../models/dashboard';
import EposNowPieChart from './PieChart';

export interface PieBarChartData {
    value: number;
    label: string;   
}

export interface PieBarChartProps {
    title: string;
    description: string;
    data: PieBarChartData[];
    currency?: string;
    locale?: string;
    isLoading?: boolean;
    error?: boolean;
    reportName?: string;
    reportLink?: string;
    height?: number;
}

const scrollCSS = {
    "&::-webkit-scrollbar": {
        width: "5px",
    },
    "&::-webkit-scrollbar-track": {
        background: "#f1f1f1",
        borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
        background: "#888",
        borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
        background: "#555",
    },
}

interface Colour {
    front: string;
    back: string;
}

const PieBarChart = (props: PieBarChartProps) => {
    const {
        title,
        description,
        data,
        currency,
        locale,
        isLoading,
        error,
        reportName,
        reportLink,
        height,
    } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const colours: Colour[] = [
        {
            front: theme.palette.blue.main,
            back: theme.palette.blue.light
        },
        {
            front: theme.palette.cyan.main,
            back: theme.palette.cyan.light
        },
        {
            front: theme.palette.teal.main,
            back: theme.palette.teal.light
        },
        {
            front: theme.palette.lightGreen.main,
            back: theme.palette.lightGreen.light
        },
        {
            front: theme.palette.lime.main,
            back: theme.palette.lime.light
        },
        {
            front: theme.palette.yellow.main,
            back: theme.palette.yellow.light
        },
        {
            front: theme.palette.orange.main,
            back: theme.palette.orange.light
        },
        {
            front: theme.palette.deepOrange.main,
            back: theme.palette.deepOrange.light
        },
        {
            front: theme.palette.fucsia.main,
            back: theme.palette.fucsia.light
        },
        {
            front: theme.palette.purple.main,
            back: theme.palette.purple.light
        }
    ];
    const getFrontColour = (index: number) => colours[index % colours.length].front;
    const getBackColour = (index: number) => colours[index % colours.length].back;
    const [chartType, setChartType] = useState<ChartType>('bar');
    const total = data?.reduce(((x, y) => x+y.value), 0) || 1;
    const noData = !data?.length
    data?.sort((a,b) => b.value - a.value);

    const toggleChart = (
        event: React.MouseEvent<HTMLElement>,
        newChartType: ChartType,
      ) => {
        if (newChartType !== null) {
            setChartType(newChartType);
        }
    };

    return (
        <Card elevation={0} sx={{
            border: `solid 1px`,
            borderColor: theme.palette.grey['12p'],
            marginX: '8px',
            padding: '12px 16px',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            height,
            maxHeight: '100%',
            breakInside: 'avoid', 
            pageBreakInside: 'avoid',
        }}>
            <Box sx={{marginRight: '4px', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                {error && <ChartBanner type='error' title={t("screens.dashboarding.dataError")} />}
                {/* {!error && noData && <ChartBanner type='warning' title={t("screens.dashboarding.noDataAvailable")} />} */}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent:'space-between', marginY: '6px' }}>
                {isLoading && <Skeleton variant='rectangular' sx={{ width: '70px', height: '60px' }} />}
                {!isLoading &&
                <>
                    <Box sx={{flex: 1}}>
                        <Typography variant='h2'>{title}</Typography>
                        <Typography variant='body2'>{description}</Typography>
                    </Box>
                    {!error && noData && (
                        <Chip
                            variant="filled"
                            size="small"
                            label={t("screens.dashboarding.dataUnavailable")}
                            icon={<WarningAmberOutlined />}
                            sx={{
                                backgroundColor: theme.palette.warning['190p'],
                                '& .MuiChip-label': {
                                    color: theme.palette.warning['160p'],
                                },
                            }}
                        />
                    )}
                    </>
                }
                <Box sx={{ display: 'flex', flexDirection: 'row', width: 'auto' }}>
                    {isLoading && <Skeleton variant='rectangular' sx={{ width: '110px', height: '60px' }} />}
                    {!isLoading && !noData &&
                        <ToggleButtonGroup color='primary' value={chartType} exclusive onChange={toggleChart}>
                            <ToggleButton value='bar' sx={{ border: 'none', borderRadius: '50% !important', margin: '5px' }}>
                                <BarChartIcon />
                            </ToggleButton>
                            <ToggleButton value='pie' sx={{ border: 'none', borderRadius: '50% !important', margin: '5px' }}>
                                <PieChartOutlineIcon />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    }
                </Box>
            </Box>
            {!error && noData && <Box sx ={{ flexGrow: 1 }} /> }
            {chartType==='bar' && 
                <Box sx={{ overflowY: 'auto', minHeight: 0, ...scrollCSS }}>
                    {isLoading && <Skeleton variant='rectangular' sx={{ height: '320px' }} />}
                    {!isLoading && 
                        <Box>
                            {data?.map((item, index) => (
                                <Box key={`${item.label}-${item.value}`} sx={{ marginRight: '10px' }}>
                                    <Box sx={{ width: 'auto', marginTop: '10px' }}>
                                        <Typography style={theme.typography.pieBarText}>{item.label as string}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent:'space-between', MarginBottom: '24px' }}>
                                        <Box sx={{ width:'100%', marginTop:'8px' }}>
                                            <LinearProgress variant="determinate" value={normalize(item.value/total)}
                                                sx={{
                                                    height: 7,
                                                    bgcolor: getBackColour(index),
                                                    borderRadius: 5,
                                                    "& .MuiLinearProgress-bar": { backgroundColor: getFrontColour(index), borderRadius: 5 }
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop:'8px' }}>
                                        <Typography variant='body1'>{formatValue(item.value, !!currency, currency, locale)}</Typography>
                                        <Typography variant='body2'>{percentageFormat(item.value/total, locale)}</Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    }
                </Box>
            }
            {chartType==='pie' && <EposNowPieChart data={data} height={height ? height - (reportName && reportLink ? 126 : 68) : undefined} />}
            {reportName && reportLink &&
                <Box sx={{display: 'flex', justifyContent: 'flex-end', marginTop: 'auto', marginBottom: '6px'}}>
                    <Button href={reportLink} sx={{fontSize: '16px', fontWeight: '600', color: 'primary.main'}}>{reportName}</Button>
                </Box>
            }
        </Card>
    );
}  

export default PieBarChart;
